import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import { PromiseStatuses } from "../../lib/utilis"
import { ConsentDTO, CreateCustomerRequestDTO, CustomerActiveDTO, CustomerDTO, CustomerFiltersDTO, EditCustomerRequestDTO, FindAllCustomersResponseDTO } from "./dto"
import { PaymentDTO } from "./payment/dto"
import { NewCustomerService } from "./service"

interface CustomerState {
    openNewCustomerModal: boolean
    createCustomerRequest: CreateCustomerRequestDTO
    createCustomerStatus: PromiseStatuses
    deleteCustomerStatus: PromiseStatuses
    editCustomerStatus: PromiseStatuses
    editCustomerRequest: EditCustomerRequestDTO
    customerFilters: CustomerFiltersDTO
    findCustomerByIdResponse?: CustomerDTO
    findCustomerByIdStatus: PromiseStatuses
    findAllCustomersResponse?: FindAllCustomersResponseDTO
    findAllCustomersStatus: PromiseStatuses
    findPurchaseHistoryResponse?: PaymentDTO[]
    findPurchaseHistoryStatus: PromiseStatuses
    openConfirmModal: boolean
    openAddCardModal: boolean
    customerId: string
    customerFilterName: string
    numberOrEmail: string
    consents: ConsentDTO
    otpRequestStatus: PromiseStatuses
    otpValidateStatus: PromiseStatuses
    otpValidateResponse: string
    changeStatusStatus: PromiseStatuses
    selectedCustomer: string
    tab: number
    findCustomerByIdsStatus: PromiseStatuses
    findCustomerByIdsResponse?: CustomerDTO[]
}

const initialState: CustomerState = {
    openNewCustomerModal: false,
    createCustomerRequest: {
        name: '',
        surname: '',
        email: '',
        vat: '',
        markup_rate: 0,
        namespace_k8s: [],
        min_quota: 0
    },
    findCustomerByIdStatus: "idle",
    findAllCustomersStatus: "idle",
    createCustomerStatus: "idle",
    openConfirmModal: false,
    findPurchaseHistoryStatus: "idle",
    deleteCustomerStatus: "idle",
    editCustomerStatus: "idle",
    customerFilters: {
        name: '',
        page: 0,
        sort: 0,
        itemsPerPage: 15,
        sortBy: ""
    },
    openAddCardModal: false,
    numberOrEmail: "",
    consents: {
        customer_consense: {
            terms_accepted: false,
            clauses_accepted: false,
            email: ''
        },
        otp: ''
    },
    customerId: "",
    customerFilterName: "",
    editCustomerRequest: {
        name: "",
        surname: "",
        markup_rate: 0,
        min_quota: 0,
        certified_mail: "",
        namespace_k8s: [],
        business_name: "",
        email: "",
        vat: "",
        stripe_ip_address: "",
        sdi: ""
    },
    otpRequestStatus: "idle",
    otpValidateStatus: "idle",
    otpValidateResponse: "",
    selectedCustomer: "",
    tab: 0,
    changeStatusStatus: "idle",
    findCustomerByIdsStatus: "idle"
}

export const createCustomer = createAsyncThunk(
    'customer/createCustomer',
    async (request: CreateCustomerRequestDTO, thunkApi): Promise<void> => {
        const customerService = NewCustomerService()

        return customerService.createCustomer(request)
    }
)

export const findAllCustomers = createAsyncThunk(
    'customer/findAllCustomers',
    async (filters: CustomerFiltersDTO, thunkApi): Promise<FindAllCustomersResponseDTO> => {
        const customerService = NewCustomerService()

        return customerService.findAllCustomers(filters)
    }
)

export const findCustomerById = createAsyncThunk(
    'customer/findCustomerById',
    async (id: string, thunkApi): Promise<CustomerDTO> => {
        const customerService = NewCustomerService()

        return customerService.findCustomerById(id)
    }
)

export const findCustomerByIds = createAsyncThunk(
    'customer/findCustomerByIds',
    async (ids: string[], thunkApi): Promise<CustomerDTO[]> => {
        const promises: Promise<CustomerDTO>[] = ids
            .filter(id => id !== null)
            .map(async (id) => {
                const customerService = NewCustomerService()
                return customerService.findCustomerById(id)
            })
        return Promise.all(promises)
    }
)

export const editCustomer = createAsyncThunk(
    'customer/editCustomer',
    async (request: { id: string, request: EditCustomerRequestDTO }, thunkApi): Promise<void> => {
        const customerService = NewCustomerService()

        return customerService.editCustomer(request.id, request.request)
    }
)

export const deleteCustomer = createAsyncThunk(
    'customer/deleteCustomer',
    async (id: string, thunkApi): Promise<void> => {
        const customerService = NewCustomerService()

        return customerService.deleteCustomer(id)
    }
)

export const changeStatus = createAsyncThunk(
    'customer/changeStatus',
    async (request: { id: string, request: CustomerActiveDTO }, thunkApi): Promise<void> => {
        const customerService = NewCustomerService()

        return customerService.changeStatus(request.id, request.request)
    }
)

export const otpRequest = createAsyncThunk(
    'customer/otpRequest',
    async (request: { mail: string, duration: number }): Promise<void> => {
        const customerService = NewCustomerService()

        return customerService.otpRequest(request)
    }
)

export const otpValidate = createAsyncThunk(
    '/customer/otpValidate',
    async (consents: ConsentDTO): Promise<string> => {
        const customerService = NewCustomerService()

        return customerService.otpValidate(consents)
    }
)

const customerSlice = createSlice({
    name: 'customer/slice',
    initialState,
    extraReducers(builder) {
        builder
            .addCase(findAllCustomers.pending, (state) => {
                state.findAllCustomersStatus = 'loading'
            })
            .addCase(findAllCustomers.fulfilled, (state, action) => {
                state.findAllCustomersStatus = 'successfully'
                state.findAllCustomersResponse = action.payload
            })
            .addCase(findAllCustomers.rejected, (state) => {
                state.findAllCustomersStatus = 'failed'
            })
            .addCase(createCustomer.pending, (state) => {
                state.createCustomerStatus = 'loading'
            })
            .addCase(createCustomer.fulfilled, (state, action) => {
                state.createCustomerStatus = 'successfully'
            })
            .addCase(createCustomer.rejected, (state) => {
                state.createCustomerStatus = 'failed'
            })
            .addCase(findCustomerById.pending, (state) => {
                state.findCustomerByIdStatus = 'loading'
            })
            .addCase(findCustomerById.fulfilled, (state, action) => {
                state.findCustomerByIdStatus = 'successfully'
                state.findCustomerByIdResponse = action.payload
            })
            .addCase(findCustomerById.rejected, (state) => {
                state.findCustomerByIdStatus = 'failed'
            })
            .addCase(editCustomer.pending, (state) => {
                state.editCustomerStatus = 'loading'
            })
            .addCase(editCustomer.fulfilled, (state, action) => {
                state.editCustomerStatus = 'successfully'
            })
            .addCase(editCustomer.rejected, (state) => {
                state.editCustomerStatus = 'failed'
            })
            .addCase(deleteCustomer.pending, (state) => {
                state.deleteCustomerStatus = 'loading'
            })
            .addCase(deleteCustomer.fulfilled, (state, action) => {
                state.deleteCustomerStatus = 'successfully'
            })
            .addCase(deleteCustomer.rejected, (state) => {
                state.deleteCustomerStatus = 'failed'
            })
            .addCase(otpRequest.pending, (state) => {
                state.otpRequestStatus = 'loading'
            })
            .addCase(otpRequest.fulfilled, (state) => {
                state.otpRequestStatus = 'successfully'
            })
            .addCase(otpRequest.rejected, (state) => {
                state.otpRequestStatus = 'failed'
            })
            .addCase(otpValidate.pending, (state => {
                state.otpValidateStatus = 'loading'
            }))
            .addCase(otpValidate.fulfilled, ((state, action) => {
                state.otpValidateStatus = 'successfully'
                state.otpValidateResponse = action.payload
            }))
            .addCase(otpValidate.rejected, (state => {
                state.otpValidateStatus = 'failed'
            }))
            .addCase(changeStatus.pending, (state) => {
                state.changeStatusStatus = 'loading'
            })
            .addCase(changeStatus.fulfilled, (state) => {
                state.changeStatusStatus = 'successfully'
            })
            .addCase(changeStatus.rejected, (state) => {
                state.changeStatusStatus = 'failed'
            })
            .addCase(findCustomerByIds.pending, (state) => {
                state.findCustomerByIdsStatus = 'loading'
            })
            .addCase(findCustomerByIds.fulfilled, (state, action) => {
                state.findCustomerByIdsStatus = 'successfully'
                state.findCustomerByIdsResponse = action.payload
            })
            .addCase(findCustomerByIds.rejected, (state) => {
                state.findCustomerByIdsStatus = 'failed'
            })
    },
    reducers: {
        setOpenNewCustomerModal: (state, action) => {
            state.openNewCustomerModal = action.payload
        },
        setCreateCustomerName: (state, action) => {
            state.createCustomerRequest.name = action.payload
        },
        setCreateCustomerSurname: (state, action) => {
            state.createCustomerRequest.surname = action.payload
        },
        setCreateCustomerEmail: (state, action) => {
            state.createCustomerRequest.email = action.payload
        },
        setCreateCustomerVat: (state, action) => {
            state.createCustomerRequest.vat = action.payload
        },
        setCreateCustomerNamespacek8s: (state, action) => {
            state.createCustomerRequest.namespace_k8s = action.payload
        },
        setCreateCustomerMarkupRate: (state, action) => {
            state.createCustomerRequest.markup_rate = action.payload
        },
        setCreateCustomerMinQuota: (state, action) => {
            state.createCustomerRequest.min_quota = action.payload
        },
        setEditCustomerName: (state, action) => {
            state.editCustomerRequest.name = action.payload
        },
        setEditCustomerSurname: (state, action) => {
            state.editCustomerRequest.surname = action.payload
        },
        setEditCustomerMarkupRate: (state, action) => {
            state.editCustomerRequest.markup_rate = action.payload
        },
        setEditCustomerMinQuota: (state, action) => {
            state.editCustomerRequest.min_quota = action.payload
        },
        setEditCustomerEmail: (state, action) => {
            state.editCustomerRequest.email = action.payload
        },
        setEditCustomerNamespace: (state, action) => {
            state.editCustomerRequest.namespace_k8s = action.payload
        },
        setEditCustomerVat: (state, action) => {
            state.editCustomerRequest.vat = action.payload
        },
        setEditCustomerBusinessName: (state, action) => {
            state.editCustomerRequest.business_name = action.payload
        },
        setEditCustomerCertifiedEmail: (state, action) => {
            state.editCustomerRequest.certified_mail = action.payload
        },
        setEditCustomerSdi: (state, action) => {
            state.editCustomerRequest.sdi = action.payload
        },
        setCreateCustomerStatus: (state, action) => {
            state.createCustomerStatus = action.payload
        },
        setEditCustomerStatus: (state, action) => {
            state.editCustomerStatus = action.payload
        },
        setChangeStatusStatus: (state, action) => {
            state.changeStatusStatus = action.payload
        },
        setEditCustomerIp: (state, action) => {
            state.editCustomerRequest.stripe_ip_address = action.payload
        },
        setOpenConfirmModal: (state, action) => {
            state.openConfirmModal = action.payload
        },
        setOpenAddCardModal: (state, action) => {
            state.openAddCardModal = action.payload
        },
        setCustomerId: (state, action) => {
            state.customerId = action.payload
        },
        setCustomerFilterName: (state, action) => {
            state.customerFilters.name = action.payload
        },
        setCustomerFilterPage: (state, action) => {
            state.customerFilters.page = action.payload
        },
        setCustomerFilterSortBy: (state, action) => {
            state.customerFilters.sortBy = action.payload
        },
        setCustomerFilterSort: (state, action) => {
            state.customerFilters.sort = action.payload
        },
        setNumberOrEmail: (state, action) => {
            state.numberOrEmail = action.payload
        },
        setClauses: (state, action) => {
            state.consents.customer_consense.clauses_accepted = action.payload
        },
        setTerms: (state, action) => {
            state.consents.customer_consense.terms_accepted = action.payload
        },
        setConsentEmail: (state, action) => {
            state.consents.customer_consense.email = action.payload
        },
        setOtpValidateStatus: (state, action) => {
            state.otpValidateStatus = action.payload
        },
        setSelectedCustomer: (state, action) => {
            state.selectedCustomer = action.payload
        },
        setTab: (state, action) => {
            state.tab = action.payload
        },
        resetCustomerCreateRequest: (state) => {
            state.createCustomerRequest = {
                name: '',
                surname: '',
                email: '',
                vat: '',
                markup_rate: 0,
                namespace_k8s: [],
                min_quota: 0
            }
        },
        setFindCustomerByIdsStatus: (state, action) => {
            state.findCustomerByIdsStatus = action.payload
        }
    },
})

export const {
    resetCustomerCreateRequest,
    setOpenConfirmModal,
    setOpenAddCardModal,
    setOpenNewCustomerModal,
    setCreateCustomerEmail,
    setCreateCustomerMarkupRate,
    setCreateCustomerName,
    setCreateCustomerSurname,
    setCreateCustomerVat,
    setCreateCustomerNamespacek8s,
    setCreateCustomerMinQuota,
    setCreateCustomerStatus,
    setEditCustomerIp,
    setEditCustomerMarkupRate,
    setEditCustomerName,
    setEditCustomerSurname,
    setEditCustomerMinQuota,
    setEditCustomerBusinessName,
    setEditCustomerEmail,
    setEditCustomerNamespace,
    setEditCustomerVat,
    setEditCustomerSdi,
    setEditCustomerCertifiedEmail,
    setCustomerId,
    setCustomerFilterName,
    setCustomerFilterPage,
    setClauses, setTerms,
    setConsentEmail,
    setSelectedCustomer,
    setOtpValidateStatus,
    setCustomerFilterSort,
    setCustomerFilterSortBy,
    setEditCustomerStatus,
    setChangeStatusStatus,
    setNumberOrEmail,
    setTab,
    setFindCustomerByIdsStatus
} = customerSlice.actions

export default customerSlice.reducer